/**
 * getSiteHeaderHeight
 *
 * Returns the height (in px) of the site's fixed/sticky header — the amount
 * of vertical space the header occupies at the top of the viewport.
 *
 * Checks the known Snackys header selectors in order.  For fixed/sticky
 * elements the visible bottom edge is used; for static/relative fallback
 * elements the offsetHeight is used.
 */

export const SITE_HEADER_SELECTORS = ['#cat-w', '#shop-nav'] as const;
const SITE_HEADER_MAX_HEIGHT = 90;

function isVisible(el: HTMLElement): boolean {
  const cs = window.getComputedStyle(el);
  return cs.display !== 'none' && cs.visibility !== 'hidden' && cs.opacity !== '0';
}

export function getSiteHeaderHeight(): number {
  const visibleHeaders = SITE_HEADER_SELECTORS
    .map((sel) => document.querySelector<HTMLElement>(sel))
    .filter((el): el is HTMLElement => el !== null && isVisible(el));

  if (visibleHeaders.length === 0) {
    return 0;
  }

  let totalOffset = 0;

  visibleHeaders.forEach((el) => {
    const rect = el.getBoundingClientRect();
    const pos = window.getComputedStyle(el).position;

    if (pos === 'fixed' || pos === 'sticky') {
      // Only count if the element is actually sitting at the top of the viewport
      if (rect.top <= 1 && rect.bottom > 0 && rect.bottom <= SITE_HEADER_MAX_HEIGHT) {
        totalOffset = Math.max(totalOffset, Math.round(rect.bottom));
      }
    } else if (totalOffset === 0) {
      // Fallback: use the element's layout height when no fixed/sticky header is found
      totalOffset = Math.round(el.offsetHeight);
    }
  });

  return totalOffset;
}
